import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Navigate, Route, Routes, useParams, useLocation, useNavigate } from 'react-router-dom';
import { api, APIError, guardSession, type User } from '@mastery/api-client-web';
import {
  AuthContext,
  SessionContext,
  type ExitGuard,
  type ExitPermit,
  type LoginRequest,
  type SessionStatus,
} from '../lib/session';
import { Layout } from '../components/Layout';
import { Home } from '../pages/Home';
import { createPageRoute } from '../components/PageRoute';
import { PageState, PageBoundary } from '../components/PageState';
import { authURL, safeReturnTo, rememberReferral, referralCode } from '../lib/navigation';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { clearBankCompletionMarkers } from '../lib/bank-session';

const WeChatLogin = lazy(async () => ({
  default: (await import('../components/WeChatLogin')).WeChatLogin,
}));
const SessionRecovery = lazy(async () => ({
  default: (await import('../components/SessionRecovery')).SessionRecovery,
}));

const Auth = createPageRoute(async () => ({ default: (await import('../pages/Auth')).Auth }), {
  standalone: true,
});
const Practice = createPageRoute(async () => ({
  default: (await import('../pages/Practice')).Practice,
}));
const BankPreview = createPageRoute(async () => ({
  default: (await import('../pages/BankPreview')).BankPreview,
}));
const WrongPage = createPageRoute(async () => ({
  default: (await import('../pages/Wrong')).WrongPage,
}));
const ProgressPage = createPageRoute(async () => ({
  default: (await import('../pages/Progress')).ProgressPage,
}));
const HistoryPage = createPageRoute(async () => ({
  default: (await import('../pages/History')).HistoryPage,
}));
const HistoryDetailPage = createPageRoute(async () => ({
  default: (await import('../pages/History')).HistoryDetailPage,
}));
const MembershipPage = createPageRoute(async () => ({
  default: (await import('../pages/Membership')).MembershipPage,
}));
const VisitorMembership = createPageRoute(async () => ({
  default: (await import('../pages/VisitorMembership')).VisitorMembership,
}));
const AttemptPage = createPageRoute(async () => ({
  default: (await import('../pages/Attempt')).AttemptPage,
}));
const AccountPage = createPageRoute(async () => ({
  default: (await import('../pages/Account')).AccountPage,
}));
const ConfirmEmailPage = createPageRoute(async () => ({
  default: (await import('../pages/ConfirmEmail')).ConfirmEmailPage,
}));
const HelpPage = createPageRoute(async () => ({
  default: (await import('../pages/Help')).HelpPage,
}));
const SupportListPage = createPageRoute(async () => ({
  default: (await import('../pages/Support')).SupportListPage,
}));
const SupportCreatePage = createPageRoute(async () => ({
  default: (await import('../pages/Support')).SupportCreatePage,
}));
const SupportDetailPage = createPageRoute(async () => ({
  default: (await import('../pages/Support')).SupportDetailPage,
}));
function AttemptRoute() {
  const { id } = useParams();
  return <AttemptPage key={id} />;
}
function LegacyBankRoute() {
  const location = useLocation();
  const suffix = location.pathname.slice('/bank-preview'.length) || '/';
  return <Navigate to={suffix + location.search + location.hash} replace />;
}
function LegacyCaeErrorRoute() {
  const location = useLocation();
  const { '*': suffix = '' } = useParams();
  const target = suffix ? `/CAE/${suffix}` : '/CAE';
  return <Navigate to={target + location.search + location.hash} replace />;
}
const SESSION_RETRY_DELAY_MS = 350;
const SESSION_LOADING_MIN_MS = 300;
const SESSION_EVENT_KEY = 'mastery:session-event';

function sessionRetryDelay(signal?: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    let timer: number;
    let abort = () => {};
    const finish = () => {
      signal?.removeEventListener('abort', abort);
      resolve();
    };
    timer = window.setTimeout(finish, SESSION_RETRY_DELAY_MS);
    if (!signal) return;
    abort = () => {
      window.clearTimeout(timer);
      signal.removeEventListener('abort', abort);
      reject(signal.reason);
    };
    if (signal.aborted) {
      abort();
      return;
    }
    signal.addEventListener('abort', abort, { once: true });
  });
}

export function App() {
  const location = useLocation();
  const pathname = location.pathname.replace(/\/+$/, '') || '/';
  const navigate = useNavigate();
  const [notice, setNotice] = useState('');
  const [loggingOut, setLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState(false);
  const logoutPending = useRef(false);
  const exitGuard = useRef<ExitGuard | undefined>(undefined);
  const registerExitGuard = useCallback((guard: ExitGuard) => {
    exitGuard.current = guard;
    return () => {
      if (exitGuard.current === guard) exitGuard.current = undefined;
    };
  }, []);
  const [connectionVersion, setConnectionVersion] = useState(0);
  const retryConnection = () => setConnectionVersion((version) => version + 1);
  useEffect(() => rememberReferral(location.search), [location.search]);
  useEffect(() => {
    const preventStandaloneF12 = (event: KeyboardEvent) => {
      if (
        event.key === 'F12' &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey &&
        !event.shiftKey
      ) {
        event.preventDefault();
      }
    };
    window.addEventListener('keydown', preventStandaloneF12);
    return () => window.removeEventListener('keydown', preventStandaloneF12);
  }, []);
  const recovery =
    pathname === '/reset-password'
      ? 'reset'
      : pathname === '/forgot-password'
        ? 'forgot'
        : undefined;
  const [user, setUser] = useState<User>(),
    [loading, setLoading] = useState(false),
    [error, setError] = useState<unknown>();
  const [sessionStatus, setSessionStatus] = useState<SessionStatus>('checking');
  const sessionStatusRef = useRef<SessionStatus>('checking');
  sessionStatusRef.current = sessionStatus;
  const sessionCheckPromise = useRef<Promise<SessionStatus> | undefined>(undefined);
  const sessionCheckSignal = useRef<AbortSignal | undefined>(undefined);
  const sessionCheckedAt = useRef(0);
  const sessionError = useRef<unknown>(undefined);
  const userRef = useRef<User | undefined>(undefined);
  userRef.current = user;
  const [reauthentication, setReauthentication] = useState<APIError>();
  const [loginPrompt, setLoginPrompt] = useState<LoginRequest>();
  const [wechatOAuthError, setWechatOAuthError] = useState<'scope'>();
  const [loginBusy, setLoginBusy] = useState(false);
  const loginSuccess = useRef<(() => void) | undefined>(undefined);
  const bootstrapController = useRef<AbortController | undefined>(undefined);
  const resolveRecovery = useRef<((recovered: boolean) => void) | undefined>(undefined);

  const oauthErrorFromURL =
    new URLSearchParams(location.search).get('wechat_oauth_error') === 'scope';
  useEffect(() => {
    if (!oauthErrorFromURL) return;
    setWechatOAuthError('scope');
    if (!user && pathname !== '/login' && pathname !== '/register') {
      setLoginPrompt({ reason: 'manual' });
    }
    const params = new URLSearchParams(location.search);
    params.delete('wechat_oauth_error');
    const query = params.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}${location.hash}`, { replace: true });
  }, [location.hash, location.pathname, location.search, navigate, oauthErrorFromURL, pathname, user]);

  useEffect(() => {
    if (!user) return undefined;
    let focusID = '';
    try {
      focusID = sessionStorage.getItem('mastery:wechat-focus') || '';
      if (focusID) sessionStorage.removeItem('mastery:wechat-focus');
    } catch {
      return undefined;
    }
    if (!focusID) return undefined;
    let attempts = 0;
    const restore = () => {
      const card = document.getElementById(focusID);
      const target = card?.matches('input,textarea,button,[tabindex]')
        ? card
        : card?.querySelector<HTMLElement>('input,textarea,button,[tabindex]');
      if (target) {
        target.focus({ preventScroll: true });
        return;
      }
      attempts += 1;
      if (attempts < 12) window.setTimeout(restore, 100);
    };
    const timer = window.setTimeout(restore, 0);
    return () => window.clearTimeout(timer);
  }, [user?.id]);
  const publishSessionEvent = useCallback((type: 'login' | 'logout', accountID?: string) => {
    try {
      localStorage.setItem(
        SESSION_EVENT_KEY,
        JSON.stringify({ type, accountID: accountID || null, at: Date.now() }),
      );
    } catch {
      // Cross-tab session notifications are best effort.
    }
  }, []);

  const runSessionCheck = useCallback(
    (signal?: AbortSignal, force = false): Promise<SessionStatus> => {
      if (
        !force &&
        sessionStatusRef.current !== 'unavailable' &&
        Date.now() - sessionCheckedAt.current < 30_000
      ) {
        return Promise.resolve(sessionStatusRef.current);
      }
      if (sessionCheckPromise.current && !sessionCheckSignal.current?.aborted) {
        return sessionCheckPromise.current;
      }
      sessionCheckPromise.current = undefined;
      sessionCheckSignal.current = signal;
      const check = (async () => {
        let lastError: unknown;
        for (let attempt = 0; attempt < 2; attempt += 1) {
          try {
            const account = await api.get<User>('/me', { signal });
            if (signal?.aborted) throw signal.reason;
            sessionError.current = undefined;
            sessionCheckedAt.current = Date.now();
            setSessionStatus('authenticated');
            setError(undefined);
            setUser(account);
            return 'authenticated' as const;
          } catch (cause) {
            if (signal?.aborted) throw cause;
            lastError = cause;
            if (
              attempt === 0 &&
              !(cause instanceof APIError && cause.status === 401 && userRef.current)
            ) {
              await sessionRetryDelay(signal);
            }
          }
        }
        sessionCheckedAt.current = Date.now();
        sessionError.current = lastError;
        if (lastError instanceof APIError && lastError.status === 401) {
          if (userRef.current) {
            setSessionStatus('unavailable');
            setError(undefined);
            return 'unavailable' as const;
          }
          setUser(undefined);
          setError(undefined);
          setSessionStatus('anonymous');
          return 'anonymous' as const;
        }
        setSessionStatus('unavailable');
        setError(lastError);
        return 'unavailable' as const;
      })();
      let shared: Promise<SessionStatus>;
      shared = check.finally(() => {
        if (sessionCheckPromise.current === shared) sessionCheckPromise.current = undefined;
        if (sessionCheckPromise.current === undefined) sessionCheckSignal.current = undefined;
      });
      sessionCheckPromise.current = shared;
      return shared;
    },
    [],
  );

  const checkSession = useCallback(() => runSessionCheck(), [runSessionCheck]);

  useEffect(() => {
    const onSessionEvent = (event: StorageEvent) => {
      if (event.key !== SESSION_EVENT_KEY || !event.newValue) return;
      let message: { type?: string; accountID?: string | null };
      try {
        message = JSON.parse(event.newValue) as typeof message;
      } catch {
        return;
      }
      const currentID = userRef.current?.id;
      if (message.type === 'logout' && (!message.accountID || message.accountID === currentID)) {
        bootstrapController.current?.abort();
        sessionCheckPromise.current = undefined;
        setUser(undefined);
        setSessionStatus('anonymous');
        setError(undefined);
        setLoading(false);
        return;
      }
      if (
        message.type === 'login' &&
        currentID &&
        message.accountID &&
        message.accountID !== currentID
      ) {
        setReauthentication(
          new APIError(409, 'account_changed', '另一个页面切换了登录账号。请重新登录后继续。'),
        );
      }
    };
    window.addEventListener('storage', onSessionEvent);
    return () => window.removeEventListener('storage', onSessionEvent);
  }, []);
  useLayoutEffect(() => {
    if (!user) return;
    const release = guardSession(
      user.id,
      (reason) =>
        new Promise<boolean>((resolve) => {
          resolveRecovery.current = resolve;
          setReauthentication(reason);
        }),
    );
    return () => {
      release();
      resolveRecovery.current?.(false);
      resolveRecovery.current = undefined;
      setReauthentication(undefined);
    };
  }, [user?.id]);
  useEffect(() => {
    setLoading(false);
    setSessionStatus('checking');
    setError(undefined);
    const controller = new AbortController();
    const loadingTimer = window.setTimeout(() => {
      if (!controller.signal.aborted) setLoading(true);
    }, SESSION_LOADING_MIN_MS);
    bootstrapController.current = controller;
    runSessionCheck(controller.signal, true)
      .catch((e) => {
        if (!controller.signal.aborted) {
          sessionError.current = e;
          setSessionStatus('unavailable');
          setError(e);
        }
      })
      .finally(() => {
        window.clearTimeout(loadingTimer);
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => {
      window.clearTimeout(loadingTimer);
      controller.abort();
    };
  }, [connectionVersion, runSessionCheck]);
  async function refresh() {
    const status = await runSessionCheck(undefined, true);
    if (status === 'unavailable') {
      throw sessionError.current ?? new APIError(0, 'session_unavailable', '登录状态暂时无法确认');
    }
  }
  async function logout() {
    if (logoutPending.current) return;
    logoutPending.current = true;
    let permit: ExitPermit | false | undefined;
    try {
      permit = await exitGuard.current?.();
      if (permit === false) return;
      setLogoutError(false);
      setLoggingOut(true);
      let switched = false;
      try {
        await api.post('/auth/logout');
      } catch (e) {
        if (!(e instanceof APIError && e.code === 'account_changed')) throw e;
        switched = true;
      }
      bootstrapController.current?.abort();
      setLoading(false);
      setError(undefined);
      if (user) clearBankCompletionMarkers(user.id);
      sessionCheckPromise.current = undefined;
      navigate('/', { replace: true, flushSync: true });
      setUser(undefined);
      sessionCheckedAt.current = Date.now();
      setSessionStatus('anonymous');
      publishSessionEvent('logout', user?.id);
      setNotice(switched ? '已离开本页原账号。另一页面的登录保持有效。' : '');
    } catch {
      setLogoutError(true);
    } finally {
      if (permit) permit.release();
      logoutPending.current = false;
      setLoggingOut(false);
    }
  }
  const requestLogin = useCallback(
    (request: LoginRequest = {}) => {
      if (user) {
        request.onSuccess?.();
        return;
      }
      if (request.resume) {
        try {
          sessionStorage.setItem('mastery:wechat-resume', JSON.stringify(request.resume));
        } catch {
          // The in-memory callback still handles desktop QR login.
        }
      }
      loginSuccess.current = request.onSuccess;
      setLoginPrompt(request);
    },
    [user],
  );
  const closeLoginPrompt = useCallback(() => {
    loginSuccess.current = undefined;
    try {
      sessionStorage.removeItem('mastery:wechat-resume');
    } catch {}
    setLoginBusy(false);
    setLoginPrompt(undefined);
    setWechatOAuthError(undefined);
  }, []);
  const finishPublicLogin = useCallback(
    (account: User) => {
      setLogoutError(false);
      bootstrapController.current?.abort();
      sessionCheckPromise.current = undefined;
      setUser(account);
      sessionCheckedAt.current = Date.now();
      sessionError.current = undefined;
      setSessionStatus('authenticated');
      setError(undefined);
      setLoading(false);
      setNotice('');
      setLoginBusy(false);
      setWechatOAuthError(undefined);
      try {
        sessionStorage.removeItem('mastery:referral');
        sessionStorage.removeItem('mastery:wechat-resume');
      } catch {}
      const afterLogin = loginSuccess.current;
      loginSuccess.current = undefined;
      setLoginPrompt(undefined);
      publishSessionEvent('login', account.id);
      afterLogin?.();
    },
    [publishSessionEvent],
  );
  const authMode =
    recovery ??
    (pathname === '/register' ? 'register' : pathname === '/login' ? 'login' : undefined);
  const returnTo = safeReturnTo(new URLSearchParams(location.search).get('returnTo'));
  if (authMode) {
    if (user && !recovery) return <Navigate to={returnTo} replace />;
    return (
      <PageBoundary>
        <Auth
          key={location.pathname + (recovery === 'reset' ? location.hash : '')}
          initialMode={authMode}
          notice={notice}
          oauthError={wechatOAuthError}
          onPasswordReset={() => {
            setLogoutError(false);
            bootstrapController.current?.abort();
            sessionCheckPromise.current = undefined;
            setLoading(false);
            setError(undefined);
            setUser(undefined);
            sessionCheckedAt.current = Date.now();
            setSessionStatus('anonymous');
          }}
          returnToAccount={!!user}
          onLogin={(account) => {
            finishPublicLogin(account);
            navigate(returnTo, {
              replace: true,
              state:
                authMode === 'register'
                  ? { flashNotice: '注册成功，24 小时免费体验已开启。' }
                  : undefined,
            });
          }}
        />
      </PageBoundary>
    );
  }
  const legacyExamParam =
    pathname === '/'
      ? new URLSearchParams(location.search).get('filter') ||
        new URLSearchParams(location.search).get('exam')
      : null;
  const legacyExam = legacyExamParam?.trim().toUpperCase();
  if (legacyExam && ['KET', 'PET', 'FCE', 'CAE'].includes(legacyExam)) {
    return <Navigate to={`/${legacyExam}`} replace />;
  }
  function protect(children: ReactNode) {
    if (!user && sessionStatus === 'checking' && !error)
      return <PageState kind="loading" title="正在确认登录状态…" />;
    if (loading && !user) return <PageState kind="loading" title="正在找回你的学习进度…" />;
    if (error && !user)
      return (
        <PageState
          title="登录状态暂时无法确认。"
          description="我们已自动重试一次，请检查连接后重新尝试；已保存的学习记录会在恢复连接后显示。"
          onRetry={retryConnection}
        />
      );
    if (!user)
      return (
        <Navigate
          to={authURL('login', location.pathname + location.search + location.hash, referralCode())}
          replace
        />
      );
    return children;
  }
  return (
    <SessionContext.Provider
      value={
        user
          ? {
              user,
              sessionStatus,
              checkSession,
              reauthenticating: !!reauthentication,
              refresh,
              registerExitGuard,
              updateUser: (account) =>
                setUser((current) => (current?.id === account.id ? account : current)),
              signedOut: (message) => {
                setLogoutError(false);
                bootstrapController.current?.abort();
                sessionCheckPromise.current = undefined;
                setLoading(false);
                setError(undefined);
                setUser(undefined);
                sessionCheckedAt.current = Date.now();
                setSessionStatus('anonymous');
                publishSessionEvent('logout', user.id);
                setNotice(message);
                navigate('/login', { replace: true });
              },
            }
          : null
      }
    >
      {/* prettier-ignore */}
      <AuthContext.Provider value={{ user, requestLogin }}>
      {loginPrompt && !user && (
        <ConfirmDialog
          busy={loginBusy}
          titleId="bank-login-title"
          initialFocus="#bank-login-title"
          onClose={closeLoginPrompt}
        >
          <h2 id="bank-login-title" tabIndex={-1}>
            登录后开始答题
          </h2>
          <p>
            题目可以先浏览；点选项、输入答案或提交练习前，请先用微信扫码登录。登录后会回到刚才的题目位置。
          </p>
          <Suspense fallback={<div className="loading-inline">正在打开微信登录…</div>}>
            <WeChatLogin
              onLogin={finishPublicLogin}
              onBusy={setLoginBusy}
              returnTo={`${location.pathname}${location.search}${location.hash}`}
              oauthError={wechatOAuthError}
            />
          </Suspense>
          <div className="login-dialog-actions">
            <button type="button" className="subtle" onClick={closeLoginPrompt}>
              先看看题目
            </button>
          </div>
        </ConfirmDialog>
      )}
      {logoutError && (
        <div className="connection-status" role="alert">
          <span>暂时没能退出登录，请检查连接后重新尝试。</span>
          <button className="subtle" onClick={logout}>
            重新退出
          </button>
        </div>
      )}
      {loggingOut && (
        <ConfirmDialog busy titleId="signing-out-title" onClose={() => {}}>
          <h2 id="signing-out-title">正在退出登录…</h2>
          <p role="status">正在关闭本次登录，请稍等。</p>
        </ConfirmDialog>
      )}
      {!!error && (
        <div className="connection-status">
          <span>登录状态暂时无法确认，请检查连接后重试。</span>
          <button className="subtle" onClick={retryConnection}>
            重新连接
          </button>
        </div>
      )}
      {reauthentication && user && (
        <Suspense fallback={<div className="loading-inline">正在恢复登录…</div>}>
          <SessionRecovery
            user={user}
            timed={pathname.startsWith('/attempts/')}
            reason={reauthentication}
            onRecovered={(u) => {
              setUser(u);
              sessionCheckedAt.current = Date.now();
              sessionError.current = undefined;
              setSessionStatus('authenticated');
              setReauthentication(undefined);
              resolveRecovery.current?.(true);
              resolveRecovery.current = undefined;
              publishSessionEvent('login', u.id);
            }}
          />
        </Suspense>
      )}
      <Routes>
        <Route element={<Layout logout={logout} />}>
          <Route index element={<BankPreview />} />
          <Route path="KET/*" element={<BankPreview />} />
          <Route path="PET/*" element={<BankPreview />} />
          <Route path="FCE/*" element={<BankPreview />} />
          <Route path="CAE/*" element={<BankPreview />} />
          <Route path="CAE错误/*" element={<LegacyCaeErrorRoute />} />
          <Route path="bank-preview/*" element={<LegacyBankRoute />} />
          <Route path="learning" element={<Home />} />
          <Route path="practice" element={<Practice />} />
          <Route path="mock" element={<Practice mock />} />
          <Route path="wrong" element={protect(<WrongPage />)} />
          <Route path="progress" element={protect(<ProgressPage />)} />
          <Route path="history/:id" element={protect(<HistoryDetailPage />)} />
          <Route path="history" element={protect(<HistoryPage />)} />
          <Route path="account" element={protect(<AccountPage />)} />
          <Route
            path="confirm-email"
            element={
              <ConfirmEmailPage
                key={location.pathname + location.hash}
                onEmailChanged={(id) => {
                  if (user?.id !== id) return;
                  bootstrapController.current?.abort();
                  setLoading(false);
                  setError(undefined);
                  setUser(undefined);
                  setNotice('邮箱已更换，请使用新邮箱和原密码登录。');
                }}
              />
            }
          />
          <Route path="help" element={<HelpPage />} />
          <Route path="help/requests" element={protect(<SupportListPage />)} />
          <Route path="help/requests/new" element={protect(<SupportCreatePage />)} />
          <Route path="help/requests/:id" element={protect(<SupportDetailPage />)} />
          <Route path="membership" element={user ? <MembershipPage /> : <VisitorMembership />} />
          <Route path="attempts/:id" element={protect(<AttemptRoute />)} />
          <Route path="*" element={<PageState kind="not-found" />} />
        </Route>
      </Routes>
      </AuthContext.Provider>
    </SessionContext.Provider>
  );
}
